/**
 * Money.
 *
 * Backend CLAUDE.md invariant 3: **money is never a float**. Amounts are an
 * integer count of minor units paired with an ISO-4217 currency code, in DB
 * columns, Go structs, protobuf messages, JSON DTOs and log lines — and here.
 *
 * Backend CLAUDE.md invariant 4: **currency is never optional and never
 * defaulted**. There is no `formatMoney(amount)` overload in this file and
 * there must never be one. A currency-less amount is not a thing this app can
 * represent, which is the point: it is the single most expensive thing to
 * retrofit.
 *
 * Minor-unit scale comes from the exponent table below. **Never assume two
 * decimal places** — JPY has none and BHD has three, and both are currencies a
 * European creator gets paid in.
 */

/**
 * The ISO-4217 minor-unit table, transcribed from the backend's
 * `internal/money` (go-wallet `d9f6442`) — the same 52 codes with the same
 * exponents, including the two-decimal block listed explicitly.
 *
 * # This is a closed set, and an absent entry is an error rather than a 2
 *
 * It used to list only the exponents that were NOT 2 and default everything
 * else, which asserted SHAPE where the requirement is MEMBERSHIP: `'XXX'`,
 * `'ZZZ'` and `'EUX'` are all three uppercase letters, so all three were
 * accepted and formatted as if they were euros (#93, mirroring
 * `go-wallet#433`). `'XXX'` is the worst of them — ISO-4217 assigns it as "no
 * currency", so it is not a typo anyone catches, and it has a plausible
 * exponent, so it computes. `CLAUDE.md` §3 says never assume two decimal
 * places; a default exponent is that assumption wearing a table's clothes.
 *
 * # The coupling this creates is deliberate
 *
 * A currency added to `internal/money` and not to this file turns that
 * customer's balance into an error instead of a figure. That is the trade taken
 * everywhere else in this repo: the alternative is rendering an unknown scale
 * as 2 and being a factor of ten out on a three-decimal currency, silently, on
 * a balance. A loud refusal is fixed by a deploy; a plausible wrong number is
 * never fixed, because nobody goes looking for it.
 *
 * # What guarantees the two agree: nothing but this comment
 *
 * `go-wallet#433` pins its table to `internal/money` with a drift test. That
 * test cannot be written here — vitest cannot read a Go map, and this repo does
 * not check out go-wallet in CI. So the commit above is the whole of the
 * guarantee, and saying so plainly is better than a check that looks like one
 * and is not.
 */
const EXPONENTS: Readonly<Record<string, number>> = {
  // Zero-decimal. XAF, XOF and XPF are the CFA and CFP francs — real spendable
  // currencies, unlike the X- codes for metals (XAU) and units of account (XDR).
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0,
  KRW: 0, PYG: 0, RWF: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0,
  XAF: 0, XOF: 0, XPF: 0,

  // Three-decimal
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,

  // Two-decimal — the common case, listed explicitly rather than defaulted.
  AUD: 2, BGN: 2, BRL: 2, CAD: 2, CHF: 2, CNY: 2, CZK: 2,
  DKK: 2, EUR: 2, GBP: 2, HKD: 2, HUF: 2, IDR: 2, ILS: 2,
  INR: 2, MXN: 2, MYR: 2, NOK: 2, NZD: 2, PHP: 2, PLN: 2,
  RON: 2, SEK: 2, SGD: 2, THB: 2, TRY: 2, USD: 2, ZAR: 2,
}

/**
 * An amount of money. `amountMinor` is a `bigint` rather than a `number`
 * because a JSON `number` silently loses precision above 2^53 minor units, and
 * a ledger that is correct except for large balances is not correct.
 */
export interface Money {
  readonly amountMinor: bigint
  readonly currency: string
}

const CODE = /^[A-Z]{3}$/

/**
 * The two ways a currency code can be refused, kept apart on purpose.
 *
 * They have different fixes and different owners. A malformed code is a bug in
 * the caller and nobody else needs to know. A well-formed code we do not hold an
 * exponent for may mean the BACKEND has opened a jurisdiction this bundle has
 * not shipped — and whoever reads that error needs to be pointed at the table
 * rather than left staring at the amount.
 */
export class UnknownCurrencyError extends Error {
  constructor(readonly currency: string) {
    super(
      CODE.test(currency)
        ? `currency ${JSON.stringify(currency)} is not in this bundle's ISO-4217 exponent ` +
            'table, so its minor-unit scale is unknown. If the backend has opened a new ' +
            'currency, src/lib/money.ts needs it too.'
        : `currency ${JSON.stringify(currency)} is not an ISO-4217 alphabetic code — three ` +
            'uppercase letters, e.g. "EUR".',
    )
    this.name = 'UnknownCurrencyError'
  }
}

/**
 * Whether this bundle holds a minor-unit scale for a code.
 *
 * Mirrors the backend's `money.Known`. Case-sensitive, deliberately: `'eur'`
 * and `'EUR'` arriving as two codes for one holding is the case split
 * `go-wallet#365` closed in the database, and a decoder that normalises quietly
 * is how it gets back in.
 */
export function isKnownCurrency(currency: string): boolean {
  return CODE.test(currency) && Object.hasOwn(EXPONENTS, currency)
}

/**
 * The minor-unit exponent for a currency.
 *
 * Refuses a code this bundle has no scale for rather than returning 2. There is
 * no honest default here: 2 is right for most currencies and wrong by a factor
 * of ten for the seven three-decimal ones and a hundred for the seventeen with
 * none, and the caller cannot tell which they got.
 */
export function exponentOf(currency: string): number {
  const exp = isKnownCurrency(currency) ? EXPONENTS[currency] : undefined
  if (exp === undefined) throw new UnknownCurrencyError(currency)
  return exp
}

/**
 * A value is only Money if both halves hold up.
 *
 * The currency check lives here rather than in each caller because this is the
 * one choke point every `Money` passes through — `formatMoney` via `split`,
 * `moneyToDTO` and `sum`. Before #93 the last two validated the AMOUNT and not
 * the CODE, so `{amountMinor: 1234n, currency: 'XXX'}` could be encoded and
 * posted to the backend, which is the wrong side of the boundary to discover it
 * on.
 */
function assertMoney(m: Money): void {
  if (typeof m.amountMinor !== 'bigint') {
    throw new TypeError('amountMinor must be a bigint — money is never a float (invariant 3)')
  }
  if (!isKnownCurrency(m.currency)) throw new UnknownCurrencyError(m.currency)
}

/**
 * Split an amount into its sign, major part and minor part, all as strings.
 *
 * This is the whole of the arithmetic, and it is integer arithmetic. Nothing in
 * this module divides, and nothing produces a `number` that a caller could add
 * to another amount.
 */
function split(m: Money): { negative: boolean; major: string; minor: string } {
  assertMoney(m)
  const exp = exponentOf(m.currency)
  const negative = m.amountMinor < 0n
  const abs = negative ? -m.amountMinor : m.amountMinor
  const digits = abs.toString().padStart(exp + 1, '0')
  return {
    negative,
    major: exp === 0 ? digits : digits.slice(0, digits.length - exp),
    minor: exp === 0 ? '' : digits.slice(digits.length - exp),
  }
}

export interface FormatOptions {
  /** BCP-47 locale for digit grouping and the decimal separator. */
  readonly locale?: string
  /** Append the currency code, e.g. `13,314.75 EUR`. Defaults to false. */
  readonly withCurrency?: boolean
  /** Force a leading `+` on positive, non-zero amounts. Defaults to false. */
  readonly signed?: boolean
}

/**
 * Format an amount for display.
 *
 * The grouping and decimal separators come from `Intl.NumberFormat` for the
 * locale, but the *digits* come from the integer split above — the number is
 * never reassembled as a float in order to be formatted, because
 * `Number(133147500000n) / 100` is exactly the bug this module exists to
 * prevent.
 */
export function formatMoney(m: Money, opts: FormatOptions = {}): string {
  const { negative, major, minor } = split(m)
  const locale = opts.locale ?? 'en-GB'

  const grouped = new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 })
    .format(BigInt(major))

  // Pull the locale's decimal separator without trusting a hardcoded '.'.
  const sep =
    new Intl.NumberFormat(locale, { minimumFractionDigits: 1 })
      .formatToParts(1.1)
      .find((p) => p.type === 'decimal')?.value ?? '.'

  let out = minor === '' ? grouped : `${grouped}${sep}${minor}`

  if (negative) out = `−${out}` // U+2212 MINUS SIGN, as the design renders it
  else if (opts.signed && m.amountMinor !== 0n) out = `+ ${out}`

  if (opts.withCurrency) out = `${out} ${m.currency}`
  return out
}

/**
 * Parse a user-entered decimal string into minor units.
 *
 * Deliberately string-based. `parseFloat('0.29') * 100` is 28.999999999999996,
 * and a cash-out that is one minor unit short because of that is a support
 * ticket a customer is right to open. Rejects anything with more fraction
 * digits than the currency has — silently truncating a third decimal on a euro
 * amount is a decision the caller has to make explicitly, not one this function
 * should make quietly.
 */
export interface ParseOptions {
  /** Defaults to `en-GB`, the same default `formatMoney` uses. */
  readonly locale?: string
}

/** The locale's decimal and grouping separators, straight from `Intl`. */
function separatorsOf(locale: string): { decimal: string; group: string } {
  try {
    const parts = new Intl.NumberFormat(locale, {
      useGrouping: true,
      minimumFractionDigits: 1,
    }).formatToParts(11111.1)
    return {
      decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
      group: parts.find((p) => p.type === 'group')?.value ?? ',',
    }
  } catch {
    return { decimal: '.', group: ',' }
  }
}

const RE_ESCAPE = /[.*+?^${}()|[\]\\]/g
const esc = (s: string): string => s.replace(RE_ESCAPE, '\\$&')

/**
 * Read a human-typed amount into exact minor units.
 *
 * # `,` is not "the grouping separator" — it is whichever one the locale says
 *
 * This used to delete `,` unconditionally before looking for a decimal point,
 * so `parseMinor('12,50', 'EUR')` returned `125000n` — one thousand two hundred
 * and fifty euros — with no error at all. Comma-decimal notation is the default
 * for French, German, Spanish, Italian, Dutch and Portuguese, which is most of
 * the European creators this wallet exists for, and nothing in the input would
 * have told them (#28).
 *
 * It also defeated the purpose of the line it sat on. That line strips no-break
 * spaces so that "a user who copies a formatted figure out of this app and
 * pastes it back must not hit 'not a decimal amount'", and it named fr-FR —
 * where `formatMoney` emits `13 314,75`. Pasting that back gave `133147500n`, a
 * silent 100x. The de-DE form, `13.314,75`, threw instead.
 *
 * # Ambiguity is refused, not resolved
 *
 * `12,50` in a comma-GROUPING locale is not a small amount written oddly. It is
 * not a valid figure at all, because grouping runs in threes. So the grouping is
 * checked for shape, and anything that is not `d{1,3}(Gddd)*` is refused rather
 * than stripped.
 *
 * That is the safety property worth having: the input that used to become 100x
 * too large now raises. Guessing more cleverly would not have been a fix.
 */
export function parseMinor(input: string, currency: string, opts: ParseOptions = {}): bigint {
  const exp = exponentOf(currency)
  const { decimal, group } = separatorsOf(opts.locale ?? 'en-GB')

  const raw = input.trim()
  const bad = (): RangeError =>
    new RangeError(`${JSON.stringify(input)} is not a decimal amount`)

  const signMatch = /^([+-]?)([\s\S]*)$/.exec(raw)
  if (signMatch === null) throw bad()
  const sign = signMatch[1] === '-' ? -1n : 1n
  const body = signMatch[2] ?? ''

  // At most one decimal separator, and it splits the number.
  const pieces = body.split(decimal)
  if (pieces.length > 2) throw bad()
  const intPart = pieces[0] ?? ''
  const frac = pieces[1] ?? ''
  if (!/^\d*$/.test(frac)) throw bad()

  // Whitespace counts as grouping wherever it appears. It is never a decimal
  // separator in any locale, and a customer typing a plain space where fr-FR
  // renders U+202F is not making a mistake worth refusing.
  const groupClass = `[\\s\\u00A0\\u202F${/\s/.test(group) ? '' : esc(group)}]`
  const digitsOnly = intPart.replace(new RegExp(groupClass, 'g'), '')
  if (!/^\d*$/.test(digitsOnly)) throw bad()

  // If any grouping was used, it has to be grouping-shaped.
  if (digitsOnly !== intPart) {
    const grouped = new RegExp(`^\\d{1,3}(?:${groupClass}\\d{3})*$`)
    if (!grouped.test(intPart)) {
      throw new RangeError(
        `${JSON.stringify(input)} is not a decimal amount: ${JSON.stringify(group)} groups ` +
          `digits in threes here, and ${JSON.stringify(decimal)} is the decimal separator. ` +
          'Refusing rather than guessing — the guess would be a hundred times out.',
      )
    }
  }

  if (digitsOnly === '' && frac === '') throw bad()
  if (frac.length > exp) {
    throw new RangeError(
      `${currency} has ${exp} minor digits; ${JSON.stringify(input)} has ${frac.length}`,
    )
  }
  const major = digitsOnly === '' ? '0' : digitsOnly
  return sign * BigInt(major + frac.padEnd(exp, '0'))
}

/** Sum amounts. Refuses to mix currencies — there is no implicit conversion. */
export function sum(amounts: readonly Money[]): Money {
  const first = amounts[0]
  if (first === undefined) {
    throw new RangeError('cannot sum an empty list: the result would have no currency (invariant 4)')
  }
  let total = 0n
  for (const a of amounts) {
    if (a.currency !== first.currency) {
      throw new RangeError(`cannot sum ${first.currency} and ${a.currency} without an explicit conversion`)
    }
    assertMoney(a)
    total += a.amountMinor
  }
  return { amountMinor: total, currency: first.currency }
}

/**
 * The wire shape, which is the backend's and not ours.
 *
 * `api/openapi.yaml` now publishes two schemas, and the asymmetry is deliberate
 * (`go-wallet#377`, closed):
 *
 *   `AmountMinorOut`  responses. `{type: string, pattern: '^-?\\d+$'}` — always
 *                     a quoted integer, on every endpoint.
 *   `AmountMinorIn`   requests. `oneOf: [string, int64]`, and the contract says
 *                     plainly: **send the string form**. The number is still
 *                     accepted only so that phone binaries which cannot be
 *                     updated keep working.
 *
 * # This file has now been wrong about it in both directions, which is the point
 *
 * It once declared the field a string when the contract said number — sound
 * reasoning, wrong fact, and it would have failed on first contact in both
 * directions (#25). It then declared it a number, correctly, and hedged: decode
 * accepted BOTH forms so that a bundle already in a customer's browser would
 * survive the contract moving. The contract moved, and every deployed bundle
 * kept reading amounts correctly with no action at all.
 *
 * So the union below is not indecision. It is the thing that made a breaking
 * change to the money encoding a non-event for this app, and it stays for the
 * next one.
 */
export interface MoneyDTO {
  readonly amount_minor: number | string
  readonly currency: string
}

/**
 * Decode an amount from the backend.
 *
 * Note what this CANNOT do: if `amount_minor` arrives as a number above
 * `Number.MAX_SAFE_INTEGER`, `JSON.parse` has already rounded it before this
 * function is reached. There is nothing to detect and nothing to recover — the
 * digits are gone. That is a property of the published contract, not of this
 * decoder, which is why the fix for it belongs in the contract.
 */
export function moneyFromDTO(dto: MoneyDTO): Money {
  if (typeof dto?.currency !== 'string') {
    throw new TypeError('money DTO must carry a currency')
  }
  const raw = dto?.amount_minor
  let minor: bigint
  if (typeof raw === 'number') {
    if (!Number.isInteger(raw)) {
      throw new TypeError(`amount_minor ${JSON.stringify(raw)} is not an integer`)
    }
    if (raw > Number.MAX_SAFE_INTEGER || raw < Number.MIN_SAFE_INTEGER) {
      // Already corrupted upstream by JSON.parse. Refusing is the only honest
      // answer: rendering it would show a customer a balance that is wrong in
      // its least significant digits and carries no sign of being wrong.
      throw new TypeError(
        `amount_minor ${raw} exceeds the safe integer range, so JSON.parse has ` +
          'already lost digits from it (go-wallet#377)',
      )
    }
    minor = BigInt(raw)
  } else if (typeof raw === 'string') {
    if (!/^-?\d+$/.test(raw)) {
      throw new TypeError(`amount_minor ${JSON.stringify(raw)} is not an integer`)
    }
    minor = BigInt(raw)
  } else {
    throw new TypeError('money DTO must carry amount_minor as a number or a string')
  }
  exponentOf(dto.currency) // validate the code before it reaches a component
  return { amountMinor: minor, currency: dto.currency }
}

/**
 * Encode an amount for the backend.
 *
 * # The precision hazard is now removed rather than guarded
 *
 * This used to be the one place where the hazard turned into silent corruption,
 * so it was the one place that refused: `Number(9007199254740993n)` is
 * `9007199254740992`, a different amount, produced with no error, on a payment
 * instruction. It threw above `Number.MAX_SAFE_INTEGER` because the wire could
 * not carry the value and a wrong instruction that cannot be built beats one
 * that is built, signed and wrong.
 *
 * `go-wallet#377` closed that gap in the contract, so the refusal is no longer
 * necessary — and it was never free. An `int64` reaches 9.22 x 10^18 while
 * `Number.MAX_SAFE_INTEGER` is 9.01 x 10^15, so everything above roughly ninety
 * trillion minor units was an amount this app declined to send for a reason
 * that no longer exists.
 *
 * `String(bigint)` is exact at every magnitude, so there is nothing left to
 * guard: no conversion to a double happens on this path at all. That is a
 * stronger position than a checked conversion, because a check can be deleted
 * and an absent conversion cannot.
 *
 * The number form is still ACCEPTED by the backend, for phone binaries that
 * cannot be updated. It is not what we send: a deprecated shape kept alive by
 * new clients is a shape nobody can ever retire.
 */
export function moneyToDTO(m: Money): MoneyDTO {
  assertMoney(m)
  return { amount_minor: m.amountMinor.toString(), currency: m.currency }
}
